import {Helmet} from '@dr.pogodin/react-helmet';
import {BaseFlagProps, showNotification} from 'platform/components';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, defaultTo, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useDeleteServiceOrderIssueNoteMutation,
  useGetServiceOrderIssueNoteQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  Checklists,
  useTenant,
  DetailTemplate,
  DetailTemplateHeader,
  printPDF,
  getUserName,
  handleApiError,
  NavigationItem,
  createHeaderActions,
  Section,
  useChecklist,
  refreshDmsDatagrid,
  WAREHOUSE_GRID_CODES,
  useFileNotification,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {buildArray, composePath, EMPTY_PLACEHOLDER, parseDate, useNavigate} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {IssueNotes} from './(sections)/IssueNotes/IssueNotes';
import {Overview} from './(sections)/Overview/Overview';

// eslint-disable-next-line complexity -- Pre-existing violation
export function ServiceOrderIssueDetail() {
  const {serviceOrderIssueNoteId} = useWarehouseParams();
  const fileNotification = useFileNotification({timeoutMs: 30000});
  const navigate = useNavigate();
  const currencyFormatter = useFormatCurrency();
  const formatDate = useDateTimeFormatter();
  const tenant = useTenant();
  const {
    data: serviceOrderIssueNote,
    isLoading: isServiceOrderIssueNoteLoading,
    isFetching: isServiceOrderIssueNoteFetching,
    isError: hasServiceOrderIssueNoteError,
  } = useGetServiceOrderIssueNoteQuery({serviceOrderIssueNoteId});

  const {data: userWhoCreated, isLoading: isUserWhoCreated} = useGetUserQuery(
    {
      id: serviceOrderIssueNote?.createdBy as string,
    },
    {skip: isNilOrEmpty(serviceOrderIssueNote?.createdBy)}
  );

  const {data: userWhoCompleted, isLoading: isUserWhoCompletedLoading} = useGetUserQuery(
    {
      id: serviceOrderIssueNote?.updatedBy as string,
    },
    {skip: isNilOrEmpty(serviceOrderIssueNote?.createdBy)}
  );

  const {
    data: serviceOrderIssueNoteAction,
    isLoading: isServiceOrderIssueNoteActionLoading,
    isError: isServiceOrderIssueNoteActionError,
  } = useGetWarehouseHeaderDynamicActionsQuery(
    {id: serviceOrderIssueNoteId, resource: 'service-order-issue-note'},
    {skip: isNilOrEmpty(serviceOrderIssueNoteId)}
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'service-order-issue-note', contextId: serviceOrderIssueNoteId},
    {skip: !serviceOrderIssueNoteId}
  );

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'service-order-issue-note',
    contextId: serviceOrderIssueNoteId,
  });

  const [putServiceOrderIssueNoteAction, {isLoading: isPutServiceOrderIssueNoteActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const [deleteServiceOrderIssueNote] = useDeleteServiceOrderIssueNoteMutation();

  const isLoading =
    isServiceOrderIssueNoteLoading ||
    isUserWhoCreated ||
    isUserWhoCompletedLoading ||
    isServiceOrderIssueNoteActionLoading;

  const isError = hasServiceOrderIssueNoteError || isServiceOrderIssueNoteActionError;

  const serviceOrderIssueTitle = `${i18n.t('page.warehouse.labels.serviceOrderIssue')} ${defaultTo(
    '',
    serviceOrderIssueNote?.number
  )}`;

  const priceWithVat = currencyFormatter(
    defaultTo(0, serviceOrderIssueNote?.sparePartsTotalPrice?.totalPurchasePrice?.withVat?.amount),
    defaultTo(
      tenant.tenantCurrency,
      serviceOrderIssueNote?.sparePartsTotalPrice?.totalPurchasePrice?.withVat?.currency
    ),
    2
  );

  const priceWithoutVat = currencyFormatter(
    defaultTo(
      0,
      serviceOrderIssueNote?.sparePartsTotalPrice?.totalPurchasePrice?.withoutVat?.amount
    ),
    defaultTo(
      tenant.tenantCurrency,
      serviceOrderIssueNote?.sparePartsTotalPrice?.totalPurchasePrice?.withoutVat?.currency
    ),
    2
  );

  const getFormattedDate = (date?: string) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDate('dateShort', parseDate(date));
  };

  const createdAt = `${i18n.t('general.labels.dateCreated')}: ${getFormattedDate(
    serviceOrderIssueNote?.createdAt
  )}`;

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(userWhoCreated)}`;

  const completedAt = match(serviceOrderIssueNote?.state)
    .returnType<string | undefined>()
    .with(
      'COMPLETED',
      always(
        `${i18n.t('general.labels.dateCompleted')}: ${getFormattedDate(
          serviceOrderIssueNote?.updatedAt
        )}`
      )
    )
    .otherwise(always(undefined));

  const completedBy = match(serviceOrderIssueNote?.state)
    .returnType<string | undefined>()
    .with(
      'COMPLETED',
      always(`${i18n.t('general.labels.completedBy')}: ${getUserName(userWhoCompleted)}`)
    )
    .otherwise(always(undefined));

  const headerFlags = () => {
    const flags = match(serviceOrderIssueNote?.state)
      .returnType<Omit<BaseFlagProps, 'size'>[]>()
      .with(
        'COMPLETED',
        always([{colorScheme: 'green', label: i18n.t('general.labels.completed')}])
      )
      .otherwise(always([{colorScheme: 'blue', label: i18n.t('general.labels.pending')}]));

    if (serviceOrderIssueNote?.partsReady) {
      flags.push({colorScheme: 'blue', isSubtle: true, label: i18n.t('general.labels.ready')});
    }

    return flags;
  };

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'prntServiceOrderIssueNote') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: serviceOrderIssueNoteId,
          originEntityPrintoutType: 'prntServiceOrderIssueNote',
        },
      })
        .unwrap()
        .then((data) => {
          const fileId = data?.printout?.fileId;
          const pdfUrl = data?.printout?.pdfUrl;

          if (fileId) {
            fileNotification.registerFile(fileId, {
              onSuccess: () => {
                if (pdfUrl) {
                  printPDF(pdfUrl);
                } else {
                  showNotification.error();
                }
              },
            });
          } else {
            showNotification.error();
          }
        })
        .then(refreshDocumentsCount)
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.serviceOrderIssueDetailDocuments, {
              params: {id: serviceOrderIssueNoteId},
            })
          );
        })
        .catch(handleApiError);
    }

    return (
      match(actionKey)
        // Confirmation is handled centrally by createHeaderActions; returning the
        // promise keeps the delete dialog open with a loading state until it settles
        .with('delete', () =>
          deleteServiceOrderIssueNote({serviceOrderIssueNoteId})
            .unwrap()
            // Non-empty options force same-tab navigation while the confirm
            // chord's Ctrl/⌘ may still be held (useNavigate would window.open)
            .then(() => navigate(composePath(warehouseRoutes.serviceOrderIssues), {replace: false}))
            .catch(handleApiError)
        )
        .otherwise(() => {
          putServiceOrderIssueNoteAction({
            id: serviceOrderIssueNoteId,
            resource: 'service-order-issue-note',
            body: {actionKey},
          })
            .unwrap()
            .then(refreshDocumentsCount)
            .then(() =>
              refreshDmsDatagrid(WAREHOUSE_GRID_CODES.issueNoteServiceOrderAddedItems, {
                deselectAll: true,
              })
            )
            .catch(handleApiError);
        })
    );
  };

  const headerActions = createHeaderActions({
    actions: serviceOrderIssueNoteAction?.actions,
    callback: handleActionClick,
    isLoading: isPutServiceOrderIssueNoteActionLoading || isPostPrintoutOnDemandLoading,
  });

  const header: DetailTemplateHeader = {
    title: serviceOrderIssueTitle,
    isEntityLoading: isServiceOrderIssueNoteFetching,
    icon: 'custom/warehouse',
    parameters: [
      defaultTo('', serviceOrderIssueNote?.number),
      serviceOrderIssueNote?.serviceOrder.variant,
      createdAt,
      createdBy,
      completedAt,
      completedBy,
    ],
    recordId: serviceOrderIssueNote?.serviceOrderIssueNoteId,
    resourceId: EntityResourceIds.serviceOrderIssues,
    controls: ['ASSIGNEE'],
    flags: headerFlags(),
    actions: headerActions,
    primaryParameter: defaultTo(undefined, priceWithVat),
    secondaryParameter: `${priceWithoutVat} ${i18n.t('general.labels.w/oVat')}`,
  };

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: serviceOrderIssueNoteId || '',
    resourceId: 'SERVICE_ORDER_ISSUE',
  });

  const navigation = buildArray<NavigationItem>([
    {
      id: 'serviceOrderIssue',
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.serviceOrderIssueDetailOverview, {
        params: {id: serviceOrderIssueNoteId},
      }),
      content: (
        <Overview
          serviceOrderIssueNote={serviceOrderIssueNote}
          isServiceOrderIssueLoading={isServiceOrderIssueNoteLoading}
          priceWithoutVat={priceWithoutVat}
          priceWithVat={priceWithVat}
          data-testid={testIds.warehouse.serviceOrderIssueDetailOverview('section.overview')}
        />
      ),
      'data-testid': testIds.warehouse.serviceOrderIssueDetailOverview('navigation.overview'),
    },
    {
      id: 'issueNotes',
      label: i18n.t('page.warehouse.labels.issueNotes'),
      href: composePath(warehouseRoutes.serviceOrderIssueDetailIssueNotes, {
        params: {id: serviceOrderIssueNoteId},
      }),
      content: (
        <IssueNotes
          data-testid={testIds.warehouse.serviceOrderIssueDetailIssueNotes('section.issueNotes')}
        />
      ),
      badge: serviceOrderIssueNote?.issueNoteCount
        ? {value: serviceOrderIssueNote.issueNoteCount, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.serviceOrderIssueDetailIssueNotes('navigation.issueNotes'),
    },
    {
      id: 'documents',
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.serviceOrderIssueDetailDocuments, {
        params: {id: serviceOrderIssueNoteId},
      }),
      content: (
        <Documents
          onDocumentAction={refreshDocumentsCount}
          data-testid={testIds.warehouse.serviceOrderIssueDetailDocuments('section.documents')}
        />
      ),
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.serviceOrderIssueDetailDocuments('navigation.documents'),
    },
  ]).whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
    id: 'serviceOrderIssueChecklists',
    label: i18n.t('entity.checklist.labels.title'),
    href: composePath(warehouseRoutes.serviceOrderIssueDetailChecklists, {
      params: {id: serviceOrderIssueNoteId},
    }),
    content: (
      <Section>
        <Checklists
          {...checklistSettings}
          contextTarget="service-order-issue-note"
          contextId={serviceOrderIssueNoteId}
        />
      </Section>
    ),
    badge: badgeSettings,
    'data-testid': testIds.warehouse.serviceOrderIssueDetailChecklists('checklists'),
  });

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.serviceOrderIssueDetail')} />
      <DetailTemplate
        key={serviceOrderIssueNoteId}
        isLoading={isLoading}
        isError={isError}
        isLocked={isPutServiceOrderIssueNoteActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.serviceOrderIssueDetailOverview('page')}
      />
    </>
  );
}
